/**
 * **D346 (e) — *which number for this person?*** The address book's chooser for a contact carrying
 * two or more numbers (`whatsapp-style-contact-picker.md` §4.6). `ContactBookSheet` opens it from a
 * tap on such a row; choosing one ticks the row on that number, and dismissing ticks nothing.
 *
 * **Which member of the popover family, by D144's split-by-what-it-is rule.** `Popover` *asks with
 * an input* (a text field around `onCancel`/`onConfirm`), `ConfirmPopover` *asks* (yes or no),
 * `AlertPopover` *answers* (one dismiss). This dialog is none of those: it is **a list the manager
 * picks one row from**, and the family already has that shape — `LanguagePopover`, the fourth
 * `.popover` sibling, filed as *a list rather than a question*. So this is a `.popover` sibling of
 * the same kind, not a variant of any of the three and not a new overlay primitive: the same
 * backdrop, the same `.popover*` rules, the same stacking note (render it through `FrameOverlay`,
 * never inside a scroller), and its option rows share `LanguagePopover`'s `.language-option`
 * treatment in `global.css`. Filling `ConfirmPopover` with a list would make its confirm button a
 * second meaning for one treatment, which is exactly what D144's rule refuses.
 *
 * **Back is not this file's.** The sheet opens it from a tap and registers its own
 * `useDismissOnBack` entry above the book's (D145), so the back gesture closes this and leaves the
 * book standing with its ticks. The backdrop and `Escape` take the same `onCancel`.
 *
 * **It renders address-book data**, so `infra/tests/web-build-hygiene.test.ts` lists it among the
 * book files and scans it exactly as it scans the sheet. Nothing here stores, sends or remembers:
 * the options arrive by prop and the choice leaves by callback.
 */
import { useEffect, useRef } from 'react'
import type { E164 } from '@selogate/core'
import type { PhoneKind } from '../../device-capabilities/contacts'
import type { Key } from '../../i18n/catalogs'

/**
 * D346 (f) — the five tokens, worded by the app's own catalogs and never by the phone's (§2: a
 * Hebrew app on an English phone would otherwise say "Mobile"). Shared with the sheet's rows and
 * the collect step's picked rows, so one token reads the same word on all three.
 */
export const PHONE_KIND_KEYS: Readonly<Record<PhoneKind, Key<'invite'>>> = {
  mobile: 'phoneKindMobile',
  home: 'phoneKindHome',
  work: 'phoneKindWork',
  main: 'phoneKindMain',
  other: 'phoneKindOther',
}

export interface NumberChoice {
  readonly phone: E164
  /** Absent where the surface supplied no type (the PWA): nothing is drawn, never a guess. */
  readonly kind?: PhoneKind
  /** Already on this gate — shown, and refused. */
  readonly member: boolean
}

export function NumberChooser({
  title,
  body,
  choices,
  t,
  onChoose,
  onCancel,
}: {
  readonly title: string
  readonly body: string
  readonly choices: readonly NumberChoice[]
  readonly t: (key: Key<'invite'>, vars?: Readonly<Record<string, string | number>>) => string
  onChoose(phone: E164): void
  onCancel(): void
}): JSX.Element {
  const firstOptionRef = useRef<HTMLButtonElement>(null)
  const firstEnabled = choices.findIndex((choice) => !choice.member)
  // Read through a ref: the sheet hands a fresh closure every render, and an effect keyed on it
  // would pull focus back to the first option each time the list behind this re-rendered.
  const onCancelRef = useRef(onCancel)
  onCancelRef.current = onCancel

  useEffect(() => {
    firstOptionRef.current?.focus()
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onCancelRef.current()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div className="popover-backdrop" onClick={onCancel}>
      <div
        className="popover"
        role="dialog"
        aria-modal="true"
        aria-labelledby="number-chooser-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="popover-title" id="number-chooser-title" style={{ unicodeBidi: 'plaintext' }}>
          {title}
        </h2>
        <p className="popover-body">{body}</p>
        <div className="language-option-list number-option-list">
          {choices.map((choice, index) => (
            <button
              key={choice.phone}
              ref={index === firstEnabled ? firstOptionRef : undefined}
              type="button"
              className="language-option number-option"
              data-phone={choice.phone}
              disabled={choice.member}
              onClick={() => onChoose(choice.phone)}
            >
              <span dir="ltr">{choice.phone}</span>
              <span className="number-option-meta">
                {choice.kind === undefined ? null : <span>{t(PHONE_KIND_KEYS[choice.kind])}</span>}
                {choice.member ? <span>{t('bulkBookAlreadyMember')}</span> : null}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
